import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type {
  CreateDiscountGroupInput,
  ListDiscountElementsInputSchema,
  ListDiscountGroupsInputSchema,
} from '@erp/rpc';
import type { z } from 'zod';
import { orpc, rpcClient } from '@/api/rpc/client';

type ListDiscountGroupsInput = z.infer<typeof ListDiscountGroupsInputSchema>;
type ListDiscountElementsInput = z.infer<
  typeof ListDiscountElementsInputSchema
>;

/** Discounts v2 group list (Discounts v2 page master table). */
export function useDiscountGroupsV2(input: ListDiscountGroupsInput) {
  return useQuery({
    ...orpc.accountsReceivable.discountsV2.listGroups.queryOptions({ input }),
    placeholderData: keepPreviousData,
  });
}

/** Flat element list across all groups (the "Elements" view). */
export function useDiscountElementsV2(
  input: ListDiscountElementsInput,
  enabled = true,
) {
  return useQuery({
    ...orpc.accountsReceivable.discountsV2.listElements.queryOptions({ input }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** One discount group with its elements (detail panel / approval card). */
export function useDiscountGroupV2(discountGroupId: string | undefined) {
  return useQuery({
    ...orpc.accountsReceivable.discountsV2.getGroup.queryOptions({
      input: { discountGroupId: discountGroupId ?? '' },
    }),
    enabled: !!discountGroupId,
  });
}

/**
 * Per-group activity timeline (creation, detail edits, element changes,
 * status transitions), newest first. Backs the review page's Activity tab.
 */
export function useDiscountGroupActivityV2(
  discountGroupId: string | undefined,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.discountsV2.getActivity.queryOptions({
      input: { discountGroupId: discountGroupId ?? '' },
    }),
    enabled: !!discountGroupId,
  });
  return { ...query, entries: query.data?.entries };
}

/**
 * Source-email descriptor for the review page's Emails rail tab: whether the
 * group came from an email, the conversation/thread id (which the email-thread
 * viewer fetches the full thread with), and the sender + subject for seeding
 * a reply.
 */
export function useDiscountGroupSourceEmailThreadV2(
  discountGroupId: string | undefined,
) {
  const query = useQuery({
    ...orpc.accountsReceivable.discountsV2.getSourceEmailThread.queryOptions({
      input: { discountGroupId: discountGroupId ?? '' },
    }),
    enabled: !!discountGroupId,
  });
  return { ...query, sourceEmail: query.data };
}

/**
 * Sales orders a group's (or a single element's) discounts were applied to.
 * Pass exactly one id; the other stays undefined.
 */
export function useDiscountUsageV2(input: {
  discountGroupId?: string;
  discountElementId?: string;
}) {
  const enabled = !!input.discountGroupId || !!input.discountElementId;
  return useQuery({
    ...orpc.accountsReceivable.discountsV2.getUsage.queryOptions({ input }),
    enabled,
  });
}

function useInvalidateDiscountGroupsV2() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.discountsV2.listGroups.key(),
    });
}

/** Create a discount group (+ elements), optionally submitting for approval. */
export function useCreateDiscountGroupV2() {
  const invalidate = useInvalidateDiscountGroupsV2();
  return useMutation(
    orpc.accountsReceivable.discountsV2.createGroup.mutationOptions({
      onSuccess: () => invalidate(),
    }),
  );
}

/** Submit a DRAFT/REJECTED group for approval. */
export function useSubmitDiscountGroupForApprovalV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.submitGroupForApproval.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Direct RBAC-backed decision (approve permission) on a group. */
export function useDecideDiscountGroupV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.decideGroup.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/**
 * One-click "Approve & Email": approve + send the approved form to the
 * requester (Cc the approver) from the org's discounts default mailbox.
 * Fails without approving when that mailbox isn't configured.
 */
export function useApproveAndEmailDiscountGroupV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.approveAndEmail.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Kill-switch: deactivate every element in a group. */
export function useDeactivateDiscountGroupV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.deactivateGroup.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Kill-switch: deactivate a single element. */
export function useDeactivateDiscountElementV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.deactivateElement.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Add one element to a DRAFT/REJECTED group (review-screen editing). */
export function useAddDiscountElementV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.addElement.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Patch one element of a DRAFT/REJECTED group (review-screen editing). */
export function useUpdateDiscountElementV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.updateElement.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Remove one element from a DRAFT/REJECTED group (review-screen editing). */
export function useDeleteDiscountElementV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.deleteElement.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Patch a DRAFT/REJECTED group's header fields (review-screen fix-ups). */
export function useUpdateDiscountGroupV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.updateGroup.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Resolve an unresolved ingested row by picking its product variant. */
export function useResolveUnresolvedLineV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.resolveUnresolvedLine.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Resolve a matched-but-priceless ingested row by entering its fixed price. */
export function useResolveUnresolvedLinePriceV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.resolveUnresolvedLinePrice.mutationOptions(
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: orpc.accountsReceivable.discountsV2.key(),
          }),
      },
    ),
  );
}

/** Drop an unresolved ingested row (kept for audit). */
export function useDismissUnresolvedLineV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.dismissUnresolvedLine.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Headers + sample rows of an uploaded price-list sheet (column picker). */
export function usePreviewPriceListUploadV2() {
  return useMutation(
    orpc.accountsReceivable.discountsV2.previewPriceListUpload.mutationOptions(),
  );
}

/** Parse an uploaded price-list spreadsheet into a discount group. */
export function useParsePriceListUploadV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.parsePriceListUpload.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/** Delete an unused DRAFT/REJECTED group (guarded server-side). */
/** Revert an unused APPROVED/PENDING_APPROVAL group to an editable DRAFT. */
export function useRevertDiscountGroupToDraftV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.revertGroupToDraft.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

export function useDeleteDiscountGroupV2() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.accountsReceivable.discountsV2.deleteGroup.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.accountsReceivable.discountsV2.key(),
        }),
    }),
  );
}

/**
 * Download the blank standardized Discount Request form (Excel) — served by
 * the backend with the calculated cells locked. Consumers chain
 * `triggerExcelDownload` in their onSuccess.
 */
export function useDownloadDiscountRequestFormV2() {
  return useMutation({
    mutationFn: () =>
      rpcClient.accountsReceivable.discountsV2.getRequestFormTemplate(),
  });
}

/**
 * On-demand analytics fetch for the Discounts page "Export CSV" menu: one
 * dataset (summary / groups / elements / usage) per call, server-capped.
 */
export function useExportDiscountAnalyticsV2() {
  return useMutation(
    orpc.accountsReceivable.discountsV2.exportAnalytics.mutationOptions(),
  );
}

/**
 * Build the "DISCOUNT REQUEST FORM - APPROVED" workbook for an approved
 * group; returns the base64 xlsx + the requester address to prefill the
 * compose panel with.
 */
export function useGenerateApprovedDiscountFormV2() {
  return useMutation(
    orpc.accountsReceivable.discountsV2.generateApprovedForm.mutationOptions(),
  );
}

export type { CreateDiscountGroupInput };
